import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as practSvc from '../../services/practitionersService'
import * as sessSvc from '../../services/sessionsService'
import * as shopSvc from '../../services/shopService'
import RequestAppointmentDialog from './components/RequestAppointmentDialog'
import { formatAppointmentTime, humaniseEventType, humaniseSessionMode } from './shared'

function formatPaymentAmount(minor, currency = 'INR') {
  const major = (minor || 0) / 100
  if (currency === 'INR') return `₹${major.toLocaleString('en-IN')}`
  if (currency === 'USD') return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  return `${currency || ''} ${major}`.trim()
}

const AvatarBase = styled.span`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 28px;
  letter-spacing: 0.05em;
  flex-shrink: 0;
  overflow: hidden;
`

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.bgMuted || 'rgba(31,58,95,0.08)'};
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`

const ChipCount = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const SessionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme?.color?.borderSoft || 'rgba(31,41,55,0.08)'};
  &:last-child {
    border-bottom: none;
  }
`

function initials(name) {
  if (!name) return '?'
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function Avatar({ src, name }) {
  const [broken, setBroken] = useState(false)
  if (src && !broken) {
    return (
      <AvatarBase>
        <AvatarImg src={src} alt="" onError={() => setBroken(true)} />
      </AvatarBase>
    )
  }
  return <AvatarBase>{initials(name)}</AvatarBase>
}

function AppointmentLine({ appt, onJoin, onPay, paying }) {
  if (!appt) return null
  const when = formatAppointmentTime(appt.startTime)
  const mode = humaniseSessionMode(appt.sessionMode)
  const type = humaniseEventType(appt.eventType)
  return (
    <SessionRow>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{when}</div>
        <Muted style={{ fontSize: 12, marginTop: 2 }}>
          {type}
          {mode ? ` · ${mode}` : ''}
          {appt.chamberRoomName ? ` · ${appt.chamberRoomName}` : ''}
        </Muted>
      </div>
      {appt.paymentBlocked ? (
        <PrimaryButton
          onClick={() => onPay && onPay(appt)}
          disabled={paying}
          style={{ padding: '6px 14px', fontSize: 13, background: '#C24D49' }}
        >
          {paying
            ? 'Opening…'
            : appt.paymentAmountMinor && appt.paymentAmountMinor > 0
              ? `Pay ${formatPaymentAmount(appt.paymentAmountMinor, appt.paymentCurrency)}`
              : 'Pay'}
        </PrimaryButton>
      ) : appt.canJoinNow ? (
        <PrimaryButton
          onClick={() =>
            onJoin &&
            onJoin(
              appt.joinUrl ||
                (appt.slug ? `/l/${appt.slug}` : `/l/${appt.eventId}`),
            )
          }
          style={{ padding: '6px 14px', fontSize: 13 }}
        >
          Join now
        </PrimaryButton>
      ) : (
        <Pill $tone={appt.status === 'cancelled' ? 'urgent' : appt.status === 'completed' ? undefined : 'success'}>
          {appt.status || 'booked'}
        </Pill>
      )}
    </SessionRow>
  )
}

export default function PractitionerDetailPage() {
  const { currentProfile } = usePatient()
  const { practitionerId } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [requestOpen, setRequestOpen] = useState(false)
  const [pastSessions, setPastSessions] = useState(null)
  const [pastLoading, setPastLoading] = useState(false)
  const [pastExpanded, setPastExpanded] = useState(false)
  const [payingId, setPayingId] = useState(null)
  const [payError, setPayError] = useState(null)

  async function payForAppt(appt) {
    if (!appt?.paymentDueId) return
    setPayingId(appt.eventId)
    setPayError(null)
    try {
      await shopSvc.payDue(currentProfile, appt.paymentDueId, {
        onSuccess: () => refresh(),
      })
    } catch (e) {
      setPayError(e?.message || 'Could not start payment.')
    } finally {
      setPayingId(null)
    }
  }

  const refresh = useCallback(async () => {
    if (!currentProfile || !practitionerId) return
    setLoading(true)
    try {
      const d = await practSvc.get(currentProfile, practitionerId)
      setDetail(d)
    } finally {
      setLoading(false)
    }
  }, [currentProfile, practitionerId])

  useEffect(() => {
    setDetail(null)
    setPastSessions(null)
    setPastExpanded(false)
    refresh()
  }, [refresh])

  async function loadPast() {
    if (!currentProfile || !practitionerId) return
    setPastLoading(true)
    try {
      const list = await practSvc.listSessions(currentProfile, practitionerId, {
        status: 'past',
        page: 1,
        pageSize: 20,
      })
      setPastSessions(list)
      setPastExpanded(true)
    } finally {
      setPastLoading(false)
    }
  }

  if (loading && !detail) {
    return (
      <ContentMaxWidth>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <LoadingSpinner />
        </div>
      </ContentMaxWidth>
    )
  }

  if (!detail) {
    return (
      <ContentMaxWidth>
        <Stack $gap="20px">
          <PageHeading>Practitioner not found</PageHeading>
          <GhostButton onClick={() => navigate('/patient-home/practitioners')}>
            Back
          </GhostButton>
        </Stack>
      </ContentMaxWidth>
    )
  }

  const s = detail.summary
  const services = detail.serviceHistory || s.serviceHistory || []
  const next = detail.nextAppointment
  const last = detail.lastAppointment
  const pastCount = detail.pastAppointmentsCount || 0

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/practitioners">Back to practitioners</BackLink>

        <Row $gap="20px" $wrap style={{ alignItems: 'flex-start' }}>
          <Avatar src={s.avatarUrl} name={s.displayName} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Pill>{s.specialty || 'Practitioner'}</Pill>
            <PageHeading style={{ marginTop: 8 }}>{s.displayName}</PageHeading>
            <Muted>
              {s.brandName ? `${s.brandName} · ` : ''}
              {detail.credentials?.length ? detail.credentials.join(', ') : ''}
              {detail.languages?.length ? ` · ${detail.languages.join(', ')}` : ''}
            </Muted>
          </div>
        </Row>

        <Row $gap="10px" $wrap>
          {detail.acceptsRequests && (
            <PrimaryButton onClick={() => setRequestOpen(true)}>
              + Request appointment
            </PrimaryButton>
          )}
          {detail.canMessage && (
            <GhostButton
              onClick={() => navigate(`/patient-home/messages/${s.practitionerId}`)}
            >
              Message
            </GhostButton>
          )}
        </Row>

        {next && (
          <Card>
            <CardTitle style={{ marginBottom: 8 }}>Upcoming appointment</CardTitle>
            <AppointmentLine
              appt={next}
              onJoin={(url) => navigate(url)}
              onPay={payForAppt}
              paying={payingId === next?.eventId}
            />
            {payError && (
              <Muted style={{ color: '#C24D49', marginTop: 8 }}>{payError}</Muted>
            )}
          </Card>
        )}

        {services.length > 0 && (
          <Card>
            <CardTitle style={{ marginBottom: 10 }}>Sessions with you</CardTitle>
            <Row $gap="6px" $wrap>
              {services.map((sh) => (
                <Chip key={sh.eventType}>
                  {humaniseEventType(sh.eventType)}
                  {sh.count > 1 && <ChipCount>×{sh.count}</ChipCount>}
                </Chip>
              ))}
            </Row>
          </Card>
        )}

        {(last || pastCount > 0) && (
          <Card>
            <Row $between style={{ marginBottom: 8 }}>
              <CardTitle style={{ margin: 0 }}>Past appointments</CardTitle>
              {pastCount > 0 && (
                <Muted style={{ fontSize: 12 }}>
                  {pastCount} total
                </Muted>
              )}
            </Row>
            {!pastExpanded ? (
              <>
                {last && <AppointmentLine appt={last} />}
                {pastCount > 1 && (
                  <GhostButton
                    onClick={loadPast}
                    disabled={pastLoading}
                    style={{ marginTop: 12 }}
                  >
                    {pastLoading ? 'Loading…' : `View all ${pastCount} past appointments`}
                  </GhostButton>
                )}
              </>
            ) : (
              <>
                {(pastSessions || []).map((appt) => (
                  <AppointmentLine key={appt.eventId} appt={appt} />
                ))}
                {!pastSessions?.length && (
                  <Muted>No past appointments to show.</Muted>
                )}
              </>
            )}
          </Card>
        )}

        {detail.bio && (
          <Card>
            <CardTitle style={{ marginBottom: 8 }}>About</CardTitle>
            <Muted style={{ lineHeight: 1.6 }}>{detail.bio}</Muted>
          </Card>
        )}
      </Stack>

      <RequestAppointmentDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSubmit={async (body) => {
          await sessSvc.requestSession(currentProfile, body)
        }}
        defaultPractitionerId={s.practitionerId}
        defaultPractitionerName={s.displayName}
      />
    </ContentMaxWidth>
  )
}
